/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useFormContext } from "react-hook-form";
import { FileUploadField } from "../fields/FileUploadField";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../colors";
import { FileUploadWrapper } from "../../FileUploadWrapper";

export enum FileUploadFormat {
  IMAGE = ".jpg,.jpeg,.png",
  VIDEO = ".mp4,.mov",
  PDF = ".pdf",
}

export const mapFileFormatToString = (fileFormat: FileUploadFormat) => {
  switch (fileFormat) {
    case FileUploadFormat.IMAGE:
      return "image";
    case FileUploadFormat.VIDEO:
      return "video";
    case FileUploadFormat.PDF:
      return "PDF";
    default:
      throw new Error(`Unsupported file format: ${fileFormat}`);
  }
};

const uploadHeaderStyle = css`
  display: flex;
  flex-direction: column;
`;

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

type Props = {
  multiple?: boolean;
  name?: string;
  accept?: FileUploadFormat;
  label?: string;
  onDrop?: () => void;
};

export const FileUploadInput = ({
  multiple = false,
  name = "files",
  accept = FileUploadFormat.IMAGE,
  label,
  ...rest
}: Props) => {
  const {
    setValue,
    watch,
    formState: { errors: formErrors },
  } = useFormContext();

  const onDrop = (acceptedFiles: File[]) => {
    setValue(name, acceptedFiles);
    rest.onDrop && rest.onDrop();
  };

  const files = watch(name);

  return (
    <div css={wrapperStyle}>
      {label && (
        <StyledText className="bodySubtextSemibold mb-0">{label}</StyledText>
      )}
      <FileUploadField accept={accept} onDrop={onDrop} multiple={multiple} />
      {files?.length > 0 && (
        <>
          <StyledText
            css={uploadHeaderStyle}
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-0"
          >
            Uploads
          </StyledText>
          {((files as File[]) || []).map((file, index) => (
            <FileUploadWrapper
              key={index}
              file={file}
              accept={accept}
              isLoading={false}
              remove={() => {
                setValue("files", []);
              }}
            />
          ))}
          <StyledText
            color={colors.textSecondary}
            className="bodySubtextRegular mb-0"
          >
            {files.length} uploaded {`${mapFileFormatToString(accept)}`}{" "}
            {files.length > 1 && "s"}
          </StyledText>
        </>
      )}
      {formErrors.files && (
        <StyledText
          color={colors.errorDark}
          className="bodySubtextRegular mb-0"
        >
          {`${formErrors.files.message}`}
        </StyledText>
      )}
    </div>
  );
};
